/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { assert } from "@fluidframework/core-utils/internal";
import { runWithRetry } from "@fluidframework/driver-utils/internal";
import { GenericError, UsageError, } from "@fluidframework/telemetry-utils/internal";
export class RetriableDocumentStorageService {
    constructor(internalStorageServiceP, logger) {
        this.internalStorageServiceP = internalStorageServiceP;
        this.logger = logger;
        this._disposed = false;
        this.internalStorageServiceP
            .then((s) => (this.internalStorageService = s))
            .catch(() => { });
    }
    get policies() {
        if (this.internalStorageService) {
            return this.internalStorageService.policies;
        }
        throw new Error("storage service not yet instantiated");
    }
    get disposed() {
        return this._disposed;
    }
    dispose() {
        this._disposed = true;
    }
    async getSnapshotTree(version, scenarioName) {
        return this.runWithRetry(async () => this.internalStorageServiceP.then(async (s) => s.getSnapshotTree(version, scenarioName)), "storage_getSnapshotTree");
    }
    async getSnapshot(snapshotFetchOptions) {
        return this.runWithRetry(async () => this.internalStorageServiceP.then(async (s) => {
            if (s.getSnapshot !== undefined) {
                return s.getSnapshot(snapshotFetchOptions);
            }
            throw new UsageError("getSnapshot api should exist on internal storage in RetriableDocStorageService class");
        }), "storage_getSnapshot");
    }
    async readBlob(id) {
        return this.runWithRetry(async () => this.internalStorageServiceP.then(async (s) => s.readBlob(id)), "storage_readBlob");
    }
    async getVersions(
    // API used below returns null
    // eslint-disable-next-line @rushstack/no-new-null
    versionId, count, scenarioName, fetchSource) {
        return this.runWithRetry(async () => this.internalStorageServiceP.then(async (s) => s.getVersions(versionId, count, scenarioName, fetchSource)), "storage_getVersions");
    }
    async uploadSummaryWithContext(summary, context) {
        // Not using retry loop here. Couple reasons:
        // 1. If client lost connectivity, then retry loop will result in uploading stale summary
        //    by stale summarizer after connectivity comes back. It will cause failures for this client and for
        //    real (new) summarizer. This problem in particular should be solved in future by supplying abort handle
        //    on all APIs and caller (ContainerRuntime.submitSummary) aborting call on loss of connectivity
        // 2. Similar, if we get 429 with retryAfter = 10 minutes, it's likely not the right call to retry summary
        //    upload in 10 minutes - it's better to keep processing ops and retry later. Though caller needs to take
        //    retryAfter into account!
        // But retry loop is required for creation flow (Container.attach)
        assert((context.referenceSequenceNumber === 0) === (context.ackHandle === undefined), 0x251 /* "creation summary has to have seq=0 && handle === undefined" */);
        if (context.referenceSequenceNumber !== 0) {
            return this.internalStorageServiceP.then(async (s) => s.uploadSummaryWithContext(summary, context));
        }
        // Creation flow with attachment blobs - need to do retries!
        return this.runWithRetry(async () => this.internalStorageServiceP.then(async (s) => s.uploadSummaryWithContext(summary, context)), "storage_uploadSummaryWithContext");
    }
    async downloadSummary(handle) {
        return this.runWithRetry(async () => this.internalStorageServiceP.then(async (s) => s.downloadSummary(handle)), "storage_downloadSummary");
    }
    async createBlob(file) {
        return this.runWithRetry(async () => this.internalStorageServiceP.then(async (s) => s.createBlob(file)), "storage_createBlob");
    }
    checkStorageDisposed(callName, error) {
        if (this._disposed) {
            this.logger.sendTelemetryEvent({
                eventName: `${callName}_abortedStorageDisposed`,
                fetchCallName: callName, // fetchCallName matches logs in runWithRetry.ts
            }, error);
            // pre-0.58 error message: storageServiceDisposedCannotRetry
            throw new GenericError("Storage Service is disposed. Cannot retry", {
                canRetry: false,
            });
        }
        return;
    }
    async runWithRetry(api, callName) {
        return runWithRetry(api, callName, this.logger, {
            onRetry: (_delayInMs, error) => this.checkStorageDisposed(callName, error),
        });
    }
}
//# sourceMappingURL=retriableDocumentStorageService.js.map